import React from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import { 
  ChartPieIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  fetchBenefitsDashboardData,
  computePeriodFinancials,
  isCountedPayment,
  getPaidAmount,
} from '../../utils/benefitsFinance';
import { BENEFITS_DASHBOARD_KEY } from '../../utils/queryKeys';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

const BenefitsOverview = () => {
  const queryClient = useQueryClient();
  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const { data: dashboardData, isLoading, refetch } = useQuery(
    BENEFITS_DASHBOARD_KEY,
    fetchBenefitsDashboardData,
    {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    }
  );

  const payments = dashboardData?.payments || [];
  const courses = dashboardData?.courses || [];
  const trainers = dashboardData?.trainers || [];
  const expenses = dashboardData?.expenses || [];
  const commissionRate = dashboardData?.commissionRate ?? 0.4;
  const financialMetrics = React.useMemo(() => {
    const period = computePeriodFinancials(
      payments,
      expenses,
      firstDayOfMonth,
      lastDayOfMonth,
      commissionRate
    );

    const revenueByMethod = period.periodPayments.reduce((acc, payment) => {
      if (!isCountedPayment(payment)) return acc;
      const method = payment.paymentMethod || payment.method || 'cash';
      const paidAmount = getPaidAmount(payment);
      acc[method] = (acc[method] || 0) + paidAmount;
      return acc;
    }, {});

    return {
      totalRevenue: period.totalIncome,
      totalExpenses: period.totalExpenses,
      netProfit: period.netProfit,
      profitMargin: period.profitMargin,
      revenueByMethod,
      paymentsCount: period.paymentsCount,
      coursesCount: courses.length,
      trainersCount: trainers.length,
      activeStudents: dashboardData?.activeStudents ?? 0,
    };
  }, [
    payments,
    expenses,
    courses.length,
    trainers.length,
    commissionRate,
    firstDayOfMonth,
    lastDayOfMonth,
    dashboardData?.activeStudents,
  ]);

  const monthlyTrendsChart = React.useMemo(() => {
    const monthsData = [];

    for (let i = 5; i >= 0; i--) {
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() - i + 1, 0, 23, 59, 59, 999);
      const period = computePeriodFinancials(
        payments,
        expenses,
        monthStart,
        monthEnd,
        commissionRate
      );

      monthsData.push({
        name: monthStart.toLocaleDateString('fr-FR', { month: 'short' }),
        revenus: period.totalIncome,
        dépenses: period.totalExpenses,
        bénéfice: period.netProfit,
      });
    }

    return monthsData;
  }, [payments, expenses, commissionRate, currentDate]);

  const incomeDistribution = React.useMemo(() => {
    const period = computePeriodFinancials(
      payments,
      expenses,
      firstDayOfMonth,
      lastDayOfMonth,
      commissionRate
    );

    const revenueByCategory = {};

    period.periodPayments.forEach((payment) => {
      if (!isCountedPayment(payment)) return;
      const validAmount = getPaidAmount(payment);
      const courseId = payment.courseId;
      const course = courses.find((c) => c.id === courseId);

      let category = 'Autres Formations';
      if (course) {
        category =
          course.category ||
          course.specialization ||
          course.field ||
          course.domain ||
          course.title ||
          category;
      }

      revenueByCategory[category] = (revenueByCategory[category] || 0) + validAmount;
    });

    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6B7280'];
    
    return Object.entries(revenueByCategory).map(([category, amount], index) => ({
      name: category,
      value: amount,
      color: colors[index % colors.length]
    }));
  }, [payments, courses, expenses, commissionRate, firstDayOfMonth, lastDayOfMonth]);

  // Extract financial data from real calculations
  const financialData = React.useMemo(() => {
    // Ensure we have valid numbers, fallback to 0 if undefined
    const totalRevenue = financialMetrics?.totalRevenue || 0;
    const totalExpenses = financialMetrics?.totalExpenses || 0;
    const netProfit = financialMetrics?.netProfit || 0;
    const profitMargin = financialMetrics?.profitMargin || 0;
    
    console.log('🔍 TOP KPI Cards (BenefitsOverview):', {
      totalRevenue: totalRevenue.toLocaleString() + ' DA',
      totalExpenses: totalExpenses.toLocaleString() + ' DA', 
      netProfit: netProfit.toLocaleString() + ' DA',
      profitMargin: profitMargin.toFixed(1) + '%'
    });
    
    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      profitMargin,
      period: `${new Date().toLocaleString('fr-FR', { month: 'long' })} ${new Date().getFullYear()}`
    };
  }, [financialMetrics]);

  // Calculate growth rates by comparing with previous month's data
  const growthRates = React.useMemo(() => {
    if (monthlyTrendsChart.length < 2) {
      return {
        revenueGrowth: 0,
        expensesGrowth: 0,
        profitGrowth: 0,
        marginGrowth: 0
      };
    }
    
    // Get current month (last in array) and previous month
    const currentMonth = monthlyTrendsChart[monthlyTrendsChart.length - 1];
    const prevMonth = monthlyTrendsChart[monthlyTrendsChart.length - 2];
    
    // Calculate growth rates
    const revenueGrowth = prevMonth.revenus > 0 
      ? ((currentMonth.revenus - prevMonth.revenus) / prevMonth.revenus * 100).toFixed(0)
      : 0;
      
    const expensesGrowth = prevMonth.dépenses > 0
      ? ((currentMonth.dépenses - prevMonth.dépenses) / prevMonth.dépenses * 100).toFixed(0)
      : 0;
      
    const profitGrowth = prevMonth.bénéfice !== 0
      ? ((currentMonth.bénéfice - prevMonth.bénéfice) / Math.abs(prevMonth.bénéfice) * 100).toFixed(0)
      : 0;
      
    // Calculate margin for both months
    const currentMargin = currentMonth.revenus > 0 
      ? (currentMonth.bénéfice / currentMonth.revenus * 100)
      : 0;
      
    const prevMargin = prevMonth.revenus > 0
      ? (prevMonth.bénéfice / prevMonth.revenus * 100)
      : 0;
      
    const marginGrowth = prevMargin !== 0
      ? ((currentMargin - prevMargin) / Math.abs(prevMargin) * 100).toFixed(0)
      : 0;
      
    return {
      revenueGrowth,
      expensesGrowth,
      profitGrowth,
      marginGrowth
    };
  }, [monthlyTrendsChart]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleManualRefresh = React.useCallback(async () => {
    await invalidateFormationGraph(queryClient);
    await refetch();
    toast.success('Données actualisées!');
  }, [queryClient, refetch]);

  return (
    <div className="space-y-6">
      {/* Manual Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={handleManualRefresh}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowPathIcon className="h-4 w-4" />
          {isLoading ? 'Chargement...' : 'Actualiser les données'}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-blue-500/30 hover:to-blue-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-300 text-sm">Revenus Formations</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">
                {isLoading ? '...' : formatCurrency(financialData.totalRevenue || 0)}
              </h3>
            </div>
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-blue-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            {parseFloat(growthRates.revenueGrowth) >= 0 ? (
              <>
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-sm ml-1">+{growthRates.revenueGrowth}%</span>
              </>
            ) : (
              <>
                <ArrowTrendingDownIcon className="h-4 w-4 text-red-400" />
                <span className="text-red-400 text-sm ml-1">{growthRates.revenueGrowth}%</span>
              </>
            )}
            <span className="text-slate-500 dark:text-slate-400 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-gradient-to-br from-red-500/20 to-red-600/20 border border-red-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-red-500/30 hover:to-red-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-300 text-sm">Coûts Formation</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">
                {isLoading ? '...' : formatCurrency(financialData.totalExpenses || 0)}
              </h3>
            </div>
            <div className="p-2 bg-red-500/20 rounded-lg">
              <ArrowTrendingDownIcon className="h-6 w-6 text-red-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            {parseFloat(growthRates.expensesGrowth) <= 0 ? (
              <>
                <ArrowTrendingDownIcon className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-sm ml-1">{growthRates.expensesGrowth}%</span>
              </>
            ) : (
              <>
                <ArrowTrendingUpIcon className="h-4 w-4 text-red-400" />
                <span className="text-red-400 text-sm ml-1">+{growthRates.expensesGrowth}%</span>
              </>
            )}
            <span className="text-slate-500 dark:text-slate-400 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-green-500/30 hover:to-green-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-300 text-sm">Bénéfice Éducatif</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">
                {isLoading ? '...' : formatCurrency(financialData.netProfit || 0)}
              </h3>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <ChartPieIcon className="h-6 w-6 text-green-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            {parseFloat(growthRates.profitGrowth) >= 0 ? (
              <>
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-sm ml-1">+{growthRates.profitGrowth}%</span>
              </>
            ) : (
              <>
                <ArrowTrendingDownIcon className="h-4 w-4 text-red-400" />
                <span className="text-red-400 text-sm ml-1">{growthRates.profitGrowth}%</span>
              </>
            )}
            <span className="text-slate-500 dark:text-slate-400 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/20 backdrop-blur-sm rounded-xl p-6 transition-all hover:from-purple-500/30 hover:to-purple-600/30"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-300 text-sm">Rentabilité Formation</p>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-1">
                {isLoading ? '...' : `${(financialData.profitMargin || 0).toFixed(1)}%`}
              </h3>
            </div>
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-purple-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            {parseFloat(growthRates.marginGrowth) >= 0 ? (
              <>
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
                <span className="text-green-400 text-sm ml-1">+{growthRates.marginGrowth}%</span>
              </>
            ) : (
              <>
                <ArrowTrendingDownIcon className="h-4 w-4 text-red-400" />
                <span className="text-red-400 text-sm ml-1">{growthRates.marginGrowth}%</span>
              </>
            )}
            <span className="text-slate-500 dark:text-slate-400 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>
      </div>

      {/* Information Panel */}
      <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600">
        <div className="flex items-center mb-4">
          <ChartPieIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Aperçu financier</h2>
        </div>
        
        <div className="bg-slate-100/80 dark:bg-slate-800/40 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-2">Période actuelle: {financialData.period}</h3>
          <p className="text-slate-600 dark:text-slate-400">
            {isLoading ? 'Chargement des données financières...' : (
              `Pour cette période, votre école de formation a généré ${formatCurrency(financialData.totalRevenue)} de revenus avec ${formatCurrency(financialData.totalExpenses)} de dépenses, 
              résultant en un bénéfice net de ${formatCurrency(financialData.netProfit)} et une marge bénéficiaire de ${financialData.profitMargin.toFixed(1)}%.`
            )}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Distribution Chart */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Répartition des revenus</h3>
          
          {isLoading || incomeDistribution.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <ChartPieIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20 mx-auto" />
                <p className="mt-4 text-slate-500 dark:text-slate-400">
                  {isLoading ? 'Chargement des données...' : 'Aucune donnée disponible pour cette période'}
                </p>
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={incomeDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {incomeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Montant']}
                    contentStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: 'rgba(255, 255, 255, 0.2)' }}
                    itemStyle={{ color: '#fff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-1/2">
                <div className="space-y-2">
                  {incomeDistribution.map((entry, index) => {
                    const totalIncome = incomeDistribution.reduce((sum, item) => sum + item.value, 0);
                    const percentage = totalIncome > 0 ? Math.round((entry.value / totalIncome) * 100) : 0;
                    
                    return (
                      <div key={index} className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: entry.color }}
                        ></div>
                        <span className="text-xs text-slate-700 dark:text-slate-300">{entry.name}</span>
                        <span className="text-xs font-medium text-slate-900 dark:text-slate-50 ml-auto">
                          {percentage}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Monthly Trends Chart */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Évolution mensuelle (Données réelles)</h3>
            <button 
              onClick={handleManualRefresh}
              className="px-3 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center gap-1"
            >
              <ArrowPathIcon className="h-3 w-3" />
              Actualiser
            </button>
          </div>
          
          {isLoading || !monthlyTrendsChart || monthlyTrendsChart.length === 0 || monthlyTrendsChart.every(month => month.revenus === 0 && month.dépenses === 0) ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <ArrowTrendingUpIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20 mx-auto" />
                <p className="mt-4 text-slate-500 dark:text-slate-400">
                  {isLoading ? 'Chargement des données...' : 'Aucune donnée de paiement disponible'}
                </p>
                {!isLoading && (
                  <div className="mt-2 text-center">
                    <p className="text-slate-400 dark:text-slate-500 text-sm mb-2">
                      Ajoutez des paiements dans la page "Paiements" pour voir l'évolution mensuelle
                    </p>
                    <button
                      onClick={handleManualRefresh}
                      className="px-3 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                    >
                      🔄 Actualiser données
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthlyTrendsChart}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" />
                  <YAxis stroke="rgba(255,255,255,0.5)" tickFormatter={(value) => {
                    if (Math.abs(value) >= 1000000) return `${(value/1000000).toFixed(1)}M`;
                    if (Math.abs(value) >= 1000) return `${(value/1000).toFixed(0)}k`;
                    return value.toString();
                  }} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                    contentStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: 'rgba(255, 255, 255, 0.2)' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="revenus" fill="#3B82F6" name="Revenus" />
                  <Bar dataKey="dépenses" fill="#EF4444" name="Dépenses" />
                  <Bar dataKey="bénéfice" fill="#10B981" name="Bénéfice" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BenefitsOverview;